import { CartLinesUpdateEvent } from '@shopify/events';
import { root, newId, acquireCart, artworkUrl, cleanProperties, readCart, changeLine, sameProperties, findLine, parseDesign, addVerified } from '@theme/pf-cart-client';
import { saveDraft, restoreDraft, clearDraft } from '@theme/pf-product-drafts';

import { validateProduct } from '@theme/pf-validation';
const forms = new WeakMap();
const messages = () => {
  try { return JSON.parse(document.getElementById('pf-design-messages')?.textContent || '{}'); }
  catch { return {}; }
};
const text = key => messages()[key] || '';
const property = el => /^properties\[([^\]]+)\]$/.exec(el?.name || '')?.[1];
const studios = form => [...form.querySelectorAll('[data-pf-studio]')];
function notice(form, key, error = false) {
  // Transient add feedback has its own announcement-only region. Keep visible
  // restoration/edit/error notices in place so adding cannot resize the form.
  const transient = !error && !forms.get(form)?.editing && ['adding_product', 'added_product'].includes(key);
  let el = form.querySelector(transient ? '[data-pf-add-notice]' : '[data-pf-edit-notice]');
  if (!transient) form.querySelector('[data-pf-add-notice]')?.replaceChildren();
  if (!el) {
    el = document.createElement('p');
    el.dataset[transient ? 'pfAddNotice' : 'pfEditNotice'] = '';
    el.className = transient ? 'pf-design-notice pf-design-notice--add-status' : 'pf-design-notice';
    el.setAttribute('role', 'status');
    el.setAttribute('aria-live', 'polite');
    if (transient) form.append(el);
    else form.prepend(el);
  }
  el.replaceChildren(document.createTextNode(text(key)));
  el.classList.toggle('is-error', error);
  if (error || forms.get(form)?.editing) {
    const link = document.createElement('a');
    link.href = root() + 'cart';
    link.textContent = text('back_cart');
    el.append(document.createTextNode(' '), link);
  }
}
export function waitForSelection(form) { notice(form, 'selection_pending'); }
function assign(el, value) {
  if (!el || value === undefined || value === null) return;
  value = String(value);
  if (el.tagName === 'SELECT' && ![...el.options].some(option => option.value === value && !option.disabled)) return;
  if (el.type === 'number' && (!Number.isFinite(Number(value)) || Number(value) < Number(el.min) || Number(value) > Number(el.max))) return;
  el.value = value;
}
function syncTemplate(studio) {
  const settings = parseDesign(studio.querySelector('[data-pf-json]')?.value);
  if (!settings) return;
  const template = studio.querySelector('[data-pf-options]');
  if (!template) return;
  // Keep saved geometry, including options omitted by the simple customer form.
  assign(template.content.querySelector('[data-pf-restored-settings]'), JSON.stringify(settings));
  assign(template.content.querySelector('[data-pf-creator-revision]'), settings.creatorRevision || 'legacy');
  assign(template.content.querySelector('[data-pf-attachment]'), settings.attachment ?? 'custom');
  assign(template.content.querySelector('[data-setting="attachment"]'), settings.attachment ?? 'custom');
  assign(template.content.querySelector('[data-pf-model-scale]'), settings.modelScale ?? 1);
  assign(template.content.querySelector('[data-pf-model-quality]'), settings.quality ?? 10);
  for (const el of template.content.querySelectorAll('[data-setting]')) {
    assign(el, el.dataset.setting === 'ring' ? (settings.ringEnabled === 'no' ? 'none' : settings.side) : settings[el.dataset.setting]);
  }
  if (studio.dataset.mode === 'logo') {
    assign(studio.querySelector('[data-setting="totalColours"]'), Number(settings.logoMaxColors) + 1);
    assign(studio.querySelector('[data-setting="logoWidth"]'), settings.logoWidth);
    const third = studio.querySelector('[data-pf-third]');
    const enabled = Number(settings.logoMaxColors) === 2;
    if (third) { third.hidden = !enabled; third.querySelectorAll('select,input').forEach(el => { el.disabled = !enabled; }); }
  }
  template.dataset.ready = 'true';
  const status = studio.querySelector('[data-pf-status]');
  if (status && studio.querySelector('[data-pf-summary]')?.value) status.textContent = studio.dataset.saved;
}
function restoreLine(form, properties) {
  const heartProfile = form.querySelector('[data-pf-heart-profile]');
  if (heartProfile) heartProfile.value = ['pf-heart-store-2026-10-27.2', 'pf-heart-store-2026-10-28.1'].includes(properties._PF_heart_profile) ? properties._PF_heart_profile : 'pf-heart-store-2026-10-27.1';
  assign(form.querySelector('[data-pf-heart-font]'), properties._PF_heart_font || 'bagel');
  for (const el of form.querySelectorAll('[name^="properties["]')) {
    const key = property(el);
    if (!key || el.type === 'file' || key.startsWith('_PF_')) continue;
    if (el.type === 'checkbox' || el.type === 'radio') el.checked = el.value === properties[key];
    else assign(el, properties[key]);
    if (el.matches('[data-pf-grid-select]')) el.dispatchEvent(new Event('change', { bubbles: true }));
  }
  for (const studio of studios(form)) {
    if (studio.dataset.mode === 'logo') {
      studio.dataset.pfSavedArtwork = artworkUrl(properties['Logo artwork']);
      if (studio.dataset.pfSavedArtwork) studio.dispatchEvent(new Event('pf:restore-artwork', { bubbles: true }));
    }
    for (const el of studio.querySelectorAll('[data-pf-saved]')) assign(el, properties[property(el)]);
    syncTemplate(studio);
  }
}
async function setup(form) {
  if (forms.has(form)) return forms.get(form);
  const state = { busy: false, blocked: false, restoring: true, editing: false };
  forms.set(form, state);
  form.inert = true;
  form.setAttribute('aria-busy', 'true');
  const query = new URLSearchParams(location.search);
  state.ready = (async () => {
    const edit = query.get('pf_edit');
    try {
      if (edit) {
        state.editing = true;
        notice(form, 'loading');
        if (edit.length > 180 || !/^\d+:[a-zA-Z0-9]+$/.test(edit)) throw new Error('line');
        const cart = await readCart();
        state.original = cart.items.find(item => item.key === edit);
        if (!state.original || String(state.original.variant_id) !== form.querySelector('[name="id"]')?.value) throw new Error('line');
        restoreLine(form, cleanProperties(state.original.properties));
        const qty = form.querySelector('[name="quantity"]');
        if (qty) { qty.value = String(state.original.quantity); qty.disabled = true; }
        const button = form.querySelector('button[name="add"]');
        const label = button?.querySelector('.add-to-cart-text__content');
        if (label) label.textContent = text('save');
        else if (button) { const node = [...button.childNodes].find(node => node.nodeType === 3 && node.textContent.trim()); if (node) node.textContent = text('save'); }
        notice(form, 'editing');
      } else {
        if (query.has('pf_new')) await clearDraft(form);
        else if (await restoreDraft(form)) notice(form, 'restored');
        studios(form).forEach(syncTemplate);
      }
    } catch { state.blocked = true; notice(form, 'not_found', true); }
    finally { state.restoring = false; form.inert = false; form.removeAttribute('aria-busy'); }
  })();
  return state;
}
function valid(form) { return validateProduct(form); }

function dataFor(form, state) {
  const data = new FormData(form);
  data.set('quantity', String(state.editing ? state.original.quantity : Number(data.get('quantity') || 1)));
  for (const [name, value] of [...data]) if (value instanceof File && !value.size) data.delete(name);
  if (state.editing) for (const [key, value] of Object.entries(state.original.properties || {})) {
    const name = 'properties[' + key + ']';
    if (!data.has(name) && ![...form.elements].some(el => el.name === name)) data.set(name, value);
  }
  if (data.has('properties[Product colour]')) { data.delete('properties[Colour & finish]'); data.delete('properties[Colour &amp; finish]'); }
  for (const studio of studios(form)) {
    const upload = studio.querySelector('[data-pf-upload]');
    if (studio.dataset.mode === 'logo' && !upload?.files.length) data.set('properties[Logo artwork]', artworkUrl(studio.dataset.pfSavedArtwork));
    for (const el of studio.querySelectorAll('[name^="properties["][disabled]')) data.delete(el.name);
  }
  const id = newId();
  data.set('properties[_PF_design_id]', id);
  const props = {};
  for (const [name, value] of data) { const key = /^properties\[([^\]]+)\]$/.exec(name)?.[1]; if (key && typeof value === 'string') props[key] = value; }
  cleanProperties(props);
  return data;
}
export async function submitProduct(form, component) {
  const state = await setup(form);
  await state.ready;
  if (state.busy || state.blocked) return;
  if (!valid(form)) return;
  const data = dataFor(form, state);
  state.busy = true;
  const buttons = [...form.querySelectorAll('button[name="add"]')];
  buttons.forEach(button => { button.disabled = true; button.setAttribute('aria-busy', 'true'); });
  notice(form, 'adding_product');
  let release;
  let pending;
  let replacementSaved = false;
  try {
    release = acquireCart();
    await saveDraft(form);
    if (state.editing) {
      const cart = await readCart();
      const current = findLine(cart, state.original);
      if (!current || !sameProperties(current.properties, state.original.properties) || current.quantity !== state.original.quantity) { notice(form, 'conflict', true); return; }
    }
    const sectionIds = [...document.querySelectorAll('cart-items-component')].map(el => el.dataset.sectionId).filter(Boolean);
    data.set('sections', [...new Set(sectionIds)].join(','));
    data.set('sections_url', location.pathname);
    if (!state.editing) {
      pending = CartLinesUpdateEvent.createPromise();
      component.dispatchEvent(new CartLinesUpdateEvent({ action: 'add', context: 'product', lines: [{ merchandiseId: data.get('id'), quantity: Number(data.get('quantity')) }], promise: pending.promise }));
    }
    const result = await addVerified(data);
    replacementSaved = state.editing;
    if (state.editing) {
      const cart = await readCart();
      const old = findLine(cart, state.original);
      if (!old || !sameProperties(old.properties, state.original.properties) || old.quantity !== state.original.quantity) {
        await changeLine(result.line, 0);
        state.blocked = true;
        notice(form, 'conflict', true);
        return;
      }
      await changeLine(old, 0);
      const verified = await readCart();
      if (findLine(verified, old) || !findLine(verified, result.line)) { state.blocked = true; notice(form, 'review_cart', true); return; }
      await clearDraft(form);
      location.assign(root() + 'cart');
      return;
    }
    state.lastLine = result.line;
    pending.resolve({ cart: CartLinesUpdateEvent.createCartFromAjaxResponse(result.cart), detail: { items: result.cart.items, sections: result.sections, source: 'pf-product-form', didError: false, itemCount: result.line.quantity } });
    for (const container of component.querySelectorAll('add-to-cart-component')) container.animateAddToCart?.();
    const actions = form.querySelector('[data-pf-after-add]');
    if (actions) { actions.hidden = false; actions.querySelector('[data-pf-add-same]').dataset.designId = result.line.properties._PF_design_id; }
    notice(form, 'added_product');
  } catch (error) {
    state.blocked = error.uncertain === true || replacementSaved;
    pending?.reject(error);
    notice(form, state.blocked ? 'review_cart' : state.editing ? 'error' : 'add_error', true);
  } finally {
    release?.();
    state.busy = false;
    buttons.forEach(button => { button.disabled = state.blocked; button.removeAttribute('aria-busy'); });
  }
}
function enhance(rootNode = document) {
  rootNode.querySelectorAll('form[data-pf-product-form]').forEach(setup);
  rootNode.querySelectorAll('[data-pf-logo-card]:not([data-ready])').forEach(card => {
    card.dataset.ready = 'true';
    const url = artworkUrl(card.dataset.artworkUrl);
    if (!url) return;
    const link = document.createElement('a');
    link.href = url; link.target = '_blank'; link.rel = 'noopener noreferrer'; link.referrerPolicy = 'no-referrer';
    const image = document.createElement('img');
    image.src = url; image.alt = text('artwork_title'); image.width = 112; image.height = 112; image.loading = 'lazy';
    link.append(image, document.createTextNode(text('view_artwork'))); card.append(link); card.hidden = false;
  });
}
document.addEventListener('input', event => {
  const form = event.target.closest?.('form[data-pf-product-form]');
  const state = forms.get(form);
  if (form && state && !state.restoring && !state.editing) queueMicrotask(() => saveDraft(form));
});
document.addEventListener('change', event => {
  const form = event.target.closest?.('form[data-pf-product-form]');
  const state = forms.get(form);
  if (form && state && !state.restoring && !state.editing) queueMicrotask(() => saveDraft(form));
});
document.addEventListener('pf:design-saved', event => {
  const form = event.target.closest?.('form[data-pf-product-form]');
  if (form && !forms.get(form)?.editing) saveDraft(form);
});
document.addEventListener('click', () => {
  queueMicrotask(() => document.querySelectorAll('form[data-pf-product-form]').forEach(form => {
    const state = forms.get(form);
    if (state && !state.restoring && !state.editing) saveDraft(form);
  }));
});
document.addEventListener('click', async event => {
  const button = event.target.closest?.('[data-pf-add-same]');
  if (!button || button.disabled) return;
  event.preventDefault();
  button.disabled = true;
  let release;
  try {
    release = acquireCart();
    const cart = await readCart();
    const line = cart.items.find(item => button.dataset.designId ? item.properties?._PF_design_id === button.dataset.designId : item.key === button.dataset.cartKey);
    if (!line) throw new Error('line');
    await changeLine(line, line.quantity + 1);
    const current = await readCart();
    const saved = findLine(current, line);
    if (!saved || saved.quantity !== line.quantity + 1 || !sameProperties(saved.properties, line.properties)) throw new Error('save');
    location.assign(root() + 'cart');
  } catch {
    const status = button.closest('[data-pf-cart-actions],[data-pf-after-add]')?.querySelector('[data-pf-action-status]');
    if (status) status.textContent = text('review_cart');
    button.disabled = false;
  } finally { release?.(); }
});
document.addEventListener('submit', event => {
  if (!event.target.matches?.('form[action*="localization"]')) return;
  const pending = [...document.querySelectorAll('form[data-pf-product-form]')].filter(form => !forms.get(form)?.editing);
  if (!pending.length) return;
  event.preventDefault();
  const submitter = event.submitter;
  Promise.all(pending.map(saveDraft)).finally(() => {
    if (submitter?.name) {
      const input = document.createElement('input'); input.type = 'hidden'; input.name = submitter.name; input.value = submitter.value; event.target.append(input);
    }
    HTMLFormElement.prototype.submit.call(event.target);
  });
}, true);
document.readyState === 'loading' ? document.addEventListener('DOMContentLoaded', () => enhance()) : enhance();
document.addEventListener('shopify:section:load', () => enhance());
const observer = new MutationObserver(() => enhance());
observer.observe(document.documentElement, { childList: true, subtree: true });
window.addEventListener('pageshow', () => enhance());

